import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTrash, 
  FaShoppingBag, 
  FaMinus, 
  FaPlus, 
  FaShieldAlt,
  FaTruck,
  FaCheckCircle,
} from 'react-icons/fa';
import StyleCreditsReminder from '../components/StyleCreditsReminder';
import StyleCreditsDisplay from '../components/StyleCreditsDisplay';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './Cart.css';

const Cart = () => {
  // Use cart context for real-time cart state
  const { 
    items: cartItems, 
    addToCart,
    updateQuantity, 
    removeFromCart, 
    getCartTotal, 
    getCartItemCount
  } = useCart();

  // Auth context
  const { currentUser } = useAuth();
  


  // Style Credits reminder state
  const [showCreditsReminder, setShowCreditsReminder] = useState(false);
  const [hasShownReminder, setHasShownReminder] = useState(false);
  

  const subtotal = getCartTotal();
  // const shipping = subtotal > 5000 ? 0 : 200; 
  const shipping = 0 // Free shipping over ₹5,000
  
  // Calculate total
  const total = Math.round(subtotal + shipping);


  // Show credits reminder for guest users with items in cart
  useEffect(() => {
    if (!currentUser && cartItems.length > 0 && !hasShownReminder && subtotal > 0) {
      const timer = setTimeout(() => {
        setShowCreditsReminder(true);
        setHasShownReminder(true);
      }, 2000); // Show after 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [currentUser, cartItems.length, hasShownReminder, subtotal]);




  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="cart-empty">
        <div className="container">
          <motion.div
            className="empty-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="empty-icon">
              <FaShoppingBag />
            </div>
            <h1>Your Cart is Empty</h1>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '24px' }}>
              <Link to="/collections" className="btn-primary">Start Shopping</Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {/* Responsive Cart Header with Equal Space Distribution */}
      <div className="cart-header">
        <div className="header-content">
          {/* Cart Title and Count - Responsive Layout */}
          <div className="cart-title-section">
            <FaShoppingBag className="cart-header-icon" />
            <h1 className="cart-title">Shopping Cart</h1>
            <span className="cart-item-count">({getCartItemCount()} {getCartItemCount() === 1 ? 'item' : 'items'})</span>
          </div>

        </div>
      </div>

      {/* Style Credits Reminder for Guest Users - Outside cart container */}
      <AnimatePresence>
        {showCreditsReminder && !currentUser && subtotal > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <StyleCreditsReminder
              showReminder={showCreditsReminder}
              onClose={() => setShowCreditsReminder(false)}
              potentialCredits={Math.max(1, Math.round(subtotal * 0.10)).toLocaleString()}
              subtotal={subtotal}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="cart-container">
        <div className="cart-content">
          {/* Cart Items */}
          <div className="cart-items-section">
            <h2>Cart Items ({cartItems.length})</h2>
                    

            <div className="cart-items">
              {cartItems.map((item, index) => (
                <motion.div
                  key={`${item.productId}-${item.size}-${item.sizeType}`}
                  className="cart-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {/* Product Image - Clean without category tag */}
                  <Link to={`/product/${item.productId}`} className="cart-item-link">
                    <div className="item-image">
                      <img src={item.image} alt={item.name} loading="lazy" />
                    </div>
                    
                    {/* Product Details - Streamlined Layout */}
                    <div className="cart-item-details">
                      {/* Product Header - Name, Type, Size with Generous Spacing */}
                      <div className="cart-item-header">
                        <h3 className="item-name">{item.name}</h3>
                        <p className="item-type">{item.type}</p>
                        
                        <div className="item-size-info">
                          <span className="size-label">Size:</span>
                          <span className="size-value">
                            {item.sizeType === 'custom' ? 'Custom' : item.size}
                          </span>
                          {/* {item.sizeType === 'custom' && (
                            <span className="custom-size-indicator">(Custom Measurements)</span>
                          )} */}
                        </div>
                        
                        {/* Separator line after size */}
                        <div className="separator-line"></div>
                        
                        {/* Action Controls - One line left to right */}
                        <div className="action-buttons-row">
                          <div className="cart-item-quantity">
                            <button 
                              className="qty-btn qty-decrease"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateQuantity(item.productId, item.size, item.sizeType, -1);
                              }}
                              disabled={item.quantity <= 1}
                              title="Decrease Quantity"
                              aria-label="Decrease quantity"
                            >
                              <FaMinus />
                            </button>
                            <div className="quantity-display-container">
                              <span className="quantity-display">{item.quantity}</span>
                            </div>
                            <button 
                              className="qty-btn qty-increase"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                updateQuantity(item.productId, item.size, item.sizeType, 1);
                              }}
                              disabled={item.quantity >= 10}
                              title="Increase Quantity"
                              aria-label="Increase quantity"
                            >
                              <FaPlus />
                            </button>
                          </div>

                          <button 
                            className="delete-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeFromCart(item.productId, item.size, item.sizeType);
                            }}
                            title="Remove Item"
                            aria-label="Remove item from cart"
                          >
                            <FaTrash />
                          </button>
                        </div>
                        
                        {/* Separator line between action buttons and price */}
                        <div className="separator-line"></div>
                        
                        {/* Price - Next line below, centered */}
                        <div className="price-row">
                          <span className="total-price-amount">₹{(item.price * item.quantity).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Premium Order Summary - Redesigned for Clear Hierarchy */}
          <div className="order-summary-section">
            <motion.div
              className="order-summary"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h2>Order Summary</h2>
              
              {/* Clean Two-Column Grid Layout */}
              <div className="summary-details">
                <div className="summary-row">
                  <span className="summary-label">Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} {cartItems.reduce((sum, item) => sum + item.quantity, 0) === 1 ? 'item' : 'items'})</span>
                  <span className="summary-value">₹{subtotal.toLocaleString()}</span>
                </div>
                
                <div className="summary-row">
                  <span className="summary-label">Shipping</span>
                  <span className={`summary-value ${shipping === 0 ? 'free-shipping' : ''}`}>
                    {shipping === 0 ? 'Free' : `₹${shipping.toLocaleString()}`}
                  </span>
                </div>
                

                
                {/* Dominant Total Row with Premium Styling */}
                <div className="summary-row total">
                  <span className="summary-label">Total</span>
                  <span className="summary-value">₹{total.toLocaleString()}</span>
                </div>
                
              </div>


              {/* Enhanced Action Buttons with Premium Styling */}
              <div className="summary-actions">
                <Link 
                  to="/checkout" 
                  className="vijh-checkout-button-unique"
                >
                  <FaShoppingBag /> Proceed to Checkout
                </Link>
              </div>

              {/* Luxury Benefits Section with Premium Typography */}
              <div className="cart-benefits">
                <div className="benefit-item">
                  <FaTruck className="benefit-icon" />
                  <div className="benefit-text">
                    <h4>FREE SHIPPING</h4>
                    <p>On orders over ₹5,000</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <FaShieldAlt className="benefit-icon" />
                  <div className="benefit-text">
                    <h4>SECURE PAYMENT</h4>
                    <p>100% secure checkout</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart; 